import { Context } from '../..';
import * as base from '../base';
import { Entity, RequiredEntity } from '../../../models/view/entity';
import { UserAssessmentStatusList } from '../../../models/view/user-assessment-status-list';
import { NotificationPriority, NotificationType } from '../../../notifications/notifications.models';
import { PagingSearchParams, SearchingSearchParams, UsersListSearchParams } from '../../../models/overmind/search-params';
import { downloadCSV } from '../../../library/helpers/download-csv';
import { SortByOption } from '../../../enums/sort-by-option';
import { AssessmentStatus } from '../../../enums/assessment-status';
import { AssessmentSubType, AssessmentType } from '../../../enums/assessment-type';
import { Score } from '../../../models/view/score';
import { ActivationStatus, AssessmentAssigneeType } from '../../../enums';

// models
export interface AssessmentListSearchParams extends SearchingSearchParams, PagingSearchParams {
  sortBy: SortByOption | undefined;
  companySlug: string;
  tag?: string;
  assessmentStatuses?: number[];
  assessmentSubType?: AssessmentSubType;
}

export interface AssessmentListItemResponse {
  assessmentId: number;
  name: string;
  slug: string;
  assessmentType: AssessmentType;
  assessmentStatus: AssessmentStatus;
  dateCreated: string;
  dateActivated: string;
  dateCompleted: string;
  // isActive: boolean; // TODO: not needed due to assessmentStatus, but returned by BE
  // isComplete: boolean; // TODO: not needed due to assessmentStatus, but returned by BE
  // isArchived: boolean; // TODO: not needed due to assessmentStatus, but returned by BE
  // isScheduled: boolean; // TODO: not needed due to assessmentStatus, but returned by BE
  tags: string[];
  average: Score;
  activationReady: boolean;
  assessmentSubType: AssessmentSubType;
}

export interface AssessmentListResponse {
  assessments: AssessmentListItemResponse[];
  totalCount: number;
  totalPages: number;
}

export interface AssessmentRequest {
  companySlug: string;
  slug: string | undefined;
  name: string;
  description: string | undefined;
  assessmentType: AssessmentType;
  assessmentStatus: AssessmentStatus;
  allowReassessment: boolean;
  forceAssessment: boolean;

  keplerPointsAvailable?: number;
  startDate?: Date;
  endDate?: Date;
  minScore?: number;
  maxScore?: number;
  assessmentSubType: AssessmentSubType;
}

export interface AssessmentResponse extends AssessmentRequest {
  slug: string;

  assigneeType: AssessmentAssigneeType;
  dateCreated: Date;
  dateCompleted: Date | undefined;
}

export interface AssessmentAssigneesRequest {
  includeLearnersWithNoDepartment: boolean;
  learnerSlugs: string[];
  teamSlugs: string[];
  departmentSlugs: string[];
  assigneeType: AssessmentAssigneeType;
  assessmentSlug: string;
}

export interface AssessmentAssigneeResponse {
  learnerSlug: string;
  firstName: string;
  lastName: string;
  departmentName: string;
  teamName: string;
  email: string;
  activationStatus: ActivationStatus;
  roleSlugs?: string[];
}

export interface AssessmentAssigneesResponse {
  includeLearnersWithNoDepartment: boolean;
  assignees: AssessmentAssigneeResponse[];
  teams: RequiredEntity[];
  departments: RequiredEntity[];
  assigneeType: AssessmentAssigneeType;
  assessmentSlug: string;
}

export interface AssessmentAssigneeListItem {
  firstName: string;
  lastName: string;
  learnerSlug: string;
  email: string;
  completionStatus: number;
  departmentName: string;
  teamName: string;
  dateServed: string;
  dateCompleted: string;
  score: Score;
  skillName: string;
  jobTitle: string;
  role: string;
}

export interface AssessmentAssigneeListResponse {
  list: AssessmentAssigneeListItem[];
  totalPages: number;
  assessmentType: AssessmentType;
  assessmentGroups: string[];
  assessmentName: string;
  learnerAssessmentCount: number;
}

// actions
const CONTROLLER = `assessments`;

export async function getAssessmentList(context: Context, payload: AssessmentListSearchParams) {
  const request: base.IRequest = { url: base.apiURL(`${CONTROLLER}${base.params(payload)}`) };
  const response: base.IResponse<AssessmentListResponse> = await base.request(request);
  return response.data;
}

export async function getAssessment({ state }: Context, payload: string): Promise<AssessmentResponse | undefined> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, 'get-assessment', state.companyVariables.slug!, payload) };
  const response: base.IResponse<AssessmentResponse> = await base.request(request);
  return response.data;
}

export async function updateAssessment({ actions }: Context, payload: AssessmentRequest): Promise<string | undefined> {
  const request: base.IRequest = {
    url: base.apiURL(CONTROLLER, `update-assessment`, payload.companySlug),
    method: 'POST',
    body: JSON.stringify(payload),
  };
  const response: base.IResponse<string> = await base.request(request);

  if (!!response.error) {
    actions.addNotification({
      active: true,
      id: crypto.randomUUID(),
      type: NotificationType.Error,
      priority: NotificationPriority.Toast,
      title: 'Something went wrong saving these details',
      message: 'Please try again',
      toastIncludeIcon: true,
    });
  }

  return response.data;
}

export async function activateAssessment({ state, actions }: Context, assessmentSlug: string): Promise<base.IResponse<AssessmentResponse>> {
  const request: base.IRequest = {
    url: base.apiURL(CONTROLLER, `activate-assessment${base.params({
      companySlug: state.companyVariables.slug!,
      assessmentSlug: assessmentSlug
    })}`), method: 'POST'
  };
  const response: base.IResponse<AssessmentResponse> = await base.request(request);

  if (response.error) {
    actions.addNotification({
      active: true,
      id: crypto.randomUUID(),
      type: NotificationType.Error,
      priority: NotificationPriority.Toast,
      title: 'Something went wrong activating this assessment',
      message: response.error.detail,
      toastIncludeIcon: true,
    });
  }

  return response;
}

export async function completeAssessment({ state }: Context, assessmentSlug: string): Promise<AssessmentResponse | undefined> {
  const request: base.IRequest = {
    url: base.apiURL(CONTROLLER, `complete-active-assessment${base.params({
      companySlug: state.companyVariables.slug!,
      assessmentSlug: assessmentSlug
    })}`), method: 'POST'
  };
  const response: base.IResponse<AssessmentResponse> = await base.request(request);
  return response.data;
}

export async function archiveAssessment({ state }: Context, assessmentSlug: string): Promise<void> {
  const request: base.IRequest = {
    url: base.apiURL(CONTROLLER, `archive-assessment${base.params({
      companySlug: state.companyVariables.slug!,
      assessmentSlug: assessmentSlug
    })}`), method: 'POST'
  };
  await base.request(request);
}

export async function resetAssessment(context: Context, payload: { assessmentSlug: string; learnerSlug?: string; emails?: string[] }): Promise<void> {
  const request: base.IRequest = {
    url: base.apiURL(CONTROLLER, `reset-assessment`, payload.assessmentSlug),
    method: 'POST',
    body: JSON.stringify({
      companySlug: context.state.companyVariables.slug,
      emails: payload.emails,
      learnerSlug: payload.learnerSlug
    }),
  };
  await base.request(request);
}

export async function getAssessmentAssignees({ state }: Context, assessmentSlug: string): Promise<AssessmentAssigneesResponse | undefined> {
  const request: base.IRequest = {
    url: base.apiURL(CONTROLLER, `get-assessment-assignees${base.params({
      companySlug: state.companyVariables.slug!,
      assessmentSlug: assessmentSlug
    })}`)
  };
  const response: base.IResponse<AssessmentAssigneesResponse> = await base.request(request);
  return response.data;
}

export async function updateAssessmentAssignees({ state, actions }: Context, payload: AssessmentAssigneesRequest): Promise<void> {
  const request: base.IRequest = {
    url: base.apiURL(CONTROLLER, `save-assessment-assignees`, state.companyVariables.slug!, payload.assessmentSlug),
    method: 'POST',
    body: JSON.stringify(payload),
  };
  const response: base.IResponse<void> = await base.request(request);

  if (!!response.error) {
    actions.addNotification({
      active: true,
      id: crypto.randomUUID(),
      type: NotificationType.Error,
      priority: NotificationPriority.Toast,
      title: 'Could not save assignees',
      message: response.error.detail,
      toastIncludeIcon: true,
    });
  }
}

export async function getAssessmentAssigneeList(context: Context, payload: { assessmentSlug: string; searchParams: UsersListSearchParams }): Promise<AssessmentAssigneeListResponse | undefined> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `users`, `${payload.assessmentSlug}${base.params(payload.searchParams)}`) };
  const response: base.IResponse<AssessmentAssigneeListResponse> = await base.request(request);
  return response.data;
}

export async function getAssessmentAssigneeListCSV(_: Context, assessmentSlug: string): Promise<void> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, 'download-assessment-learners-list', assessmentSlug), method: 'POST' };
  const response: base.IResponse<string> = await base.request(request);
  if (!response.data) return;
  await downloadCSV(response.data, `assessment-progress-${assessmentSlug}`);
}

export async function completeAssigneeAssessment({ actions }: Context, props: { assessmentSlug: string; userId: string }): Promise<UserAssessmentStatusList | undefined> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, 'complete-learner-assessment', props.assessmentSlug, props.userId), method: 'POST' };
  const response = await base.request(request);

  if (response.error) {
    actions.addNotification({
      title: `Couldn't mark learner as complete`,
      message: `We were unable to mark this learner as complete`,
      active: true,
      id: `${props.userId}_${Date.now()}`,
      priority: NotificationPriority.Toast,
      type: NotificationType.Error,
    });
  }

  return response.data;
}

export async function getActiveAssessments({ state }: Context): Promise<Entity[] | undefined> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, 'get-active-assessment-list', state.companyVariables.slug!) };
  const response: base.IResponse<Entity[]> = await base.request(request);
  return response.data;
}
